// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { Portal, useMergeRefs, useUniqueId } from '@cloudscape-design/component-toolkit/internal';
import { getAnalyticsMetadataAttribute } from '@cloudscape-design/component-toolkit/internal/analytics-metadata';
import InternalBox from '../box/internal';
import { InternalButton } from '../button/internal';
import { BuiltInErrorBoundary } from '../error-boundary/internal';
import InternalHeader from '../header/internal';
import { useInternalI18n } from '../i18n/context';
import { PerformanceMetrics } from '../internal/analytics';
import { FunnelNameSelectorContext, } from '../internal/analytics/context/analytics-context';
import { useFunnel, useFunnelStep, useFunnelSubStep } from '../internal/analytics/hooks/use-funnel';
import { getBaseProps } from '../internal/base-component';
import FocusLock from '../internal/components/focus-lock';
import { ButtonContext } from '../internal/context/button-context';
import { ModalContext } from '../internal/context/modal-context';
import ResetContextsForModal from '../internal/context/reset-contexts-for-modal';
import { fireNonCancelableEvent } from '../internal/events';
import { useContainerBreakpoints } from '../internal/hooks/container-queries';
import { useIntersectionObserver } from '../internal/hooks/use-intersection-observer';
import { useVisualRefresh } from '../internal/hooks/use-visual-mode';
import { KeyCode } from '../internal/keycode';
import { disableBodyScrolling, enableBodyScrolling } from './body-scroll';
import { useModalDimensions } from './use-modal-dimensions';
import analyticsSelectors from './analytics-metadata/styles.css.js';
import styles from './styles.css.js';
export function InternalModalAsFunnel(props) {
    const { funnelProps, funnelSubmit, funnelNextOrSubmitAttempt } = useFunnel();
    const { funnelStepProps } = useFunnelStep();
    const { subStepRef, funnelSubStepProps } = useFunnelSubStep();
    const onButtonClick = ({ variant }) => {
        if (variant === 'primary') {
            funnelNextOrSubmitAttempt();
            funnelSubmit();
        }
    };
    return (React.createElement(InternalModal, { __funnelProps: funnelProps, __funnelStepProps: funnelStepProps, __subStepRef: subStepRef, __subStepFunnelProps: funnelSubStepProps, onButtonClick: onButtonClick, ...props }));
}
export default function InternalModal({ modalRoot, getModalRoot, removeModalRoot, ...rest }) {
    return (React.createElement(Portal, { container: modalRoot, getContainer: getModalRoot, removeContainer: removeModalRoot },
        React.createElement(PortaledModal, { ...rest })));
}
// Separate component to prevent the Portal from getting in the way of refs, as it needs extra cycles to render the inner components.
// useContainerQuery needs its targeted element to exist on the first render in order to work properly.
function PortaledModal({ size, visible, header, children, footer, disableContentPaddings, position = 'center', onButtonClick = () => { }, onDismiss, width, height, __internalRootRef, __injectAnalyticsComponentMetadata, __funnelProps, __funnelStepProps, __subStepRef, __subStepFunnelProps, referrerId, ...rest }) {
    var _a;
    const instanceUniqueId = useUniqueId();
    const headerId = `${rest.id || instanceUniqueId}-header`;
    const lastMouseDownElementRef = useRef(null);
    const [breakpoint, breakpointsRef] = useContainerBreakpoints(['xs']);
    const i18n = useInternalI18n('modal');
    const closeAriaLabel = i18n('closeAriaLabel', rest.closeAriaLabel);
    const refObject = useRef(null);
    const mergedRef = useMergeRefs(breakpointsRef, refObject, __internalRootRef);
    const isRefresh = useVisualRefresh();
    const baseProps = getBaseProps(rest);
    const analyticsComponentMetadata = {
        name: 'awsui.Modal',
        label: `.${analyticsSelectors.header} h2`,
    };
    const metadataAttribute = __injectAnalyticsComponentMetadata
        ? getAnalyticsMetadataAttribute({ component: analyticsComponentMetadata })
        : {};
    const loadStartTime = useRef(0);
    const loadCompleteTime = useRef(0);
    const componentLoadingCount = useRef(0);
    const performanceMetricLogged = useRef(false);
    // enable body scroll and restore focus if unmounting while visible
    useEffect(() => {
        return () => {
            enableBodyScrolling();
        };
    }, []);
    const resetModalPerformanceData = () => {
        loadStartTime.current = performance.now();
        loadCompleteTime.current = 0;
        performanceMetricLogged.current = false;
    };
    const emitTimeToContentReadyInModal = (loadCompleteTime) => {
        var _a;
        if (componentLoadingCount.current === 0 &&
            loadStartTime.current &&
            loadStartTime.current !== 0 &&
            !performanceMetricLogged.current) {
            const timeToContentReadyInModal = loadCompleteTime - loadStartTime.current;
            PerformanceMetrics.modalPerformanceData({
                timeToContentReadyInModal,
                instanceIdentifier: instanceUniqueId,
                componentIdentifier: ((_a = headerTextRef.current) === null || _a === void 0 ? void 0 : _a.textContent) || '',
            });
            performanceMetricLogged.current = true;
        }
    };
    const MODAL_READY_TIMEOUT = 100;
    /**
     * This useEffect is triggered when the visible attribute of modal changes.
     * When modal becomes visible, modal performance metrics are reset marking the beginning loading process.
     * To ensure that the modal component ready metric is always emitted, a setTimeout is implemented.
     * This setTimeout automatically emits the component ready metric after a specified duration.
     */
    useEffect(() => {
        if (visible) {
            disableBodyScrolling();
            resetModalPerformanceData();
            setTimeout(() => {
                emitTimeToContentReadyInModal(loadStartTime.current);
            }, MODAL_READY_TIMEOUT);
        }
        else {
            enableBodyScrolling();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);
    // Because we hide the element with styles (and not actually detach it from DOM), we need to scroll to top
    useEffect(() => {
        if (visible && refObject.current) {
            refObject.current.scrollTop = 0;
        }
    }, [visible]);
    const dismiss = (reason) => fireNonCancelableEvent(onDismiss, { reason });
    const onOverlayMouseDown = (event) => {
        lastMouseDownElementRef.current = event.target;
    };
    const onOverlayClick = (event) => {
        const overlay = refObject.current;
        const lastClicked = lastMouseDownElementRef.current;
        if (event.target === overlay && lastClicked === overlay) {
            dismiss('overlay');
        }
    };
    const onCloseButtonClick = () => dismiss('closeButton');
    const escKeyHandler = (event) => {
        if (event.keyCode === KeyCode.escape) {
            dismiss('keyboard');
        }
    };
    // We use an empty div element at the end of the content slot as a sentinel
    // to detect when the user has scrolled to the bottom.
    const { ref: stickySentinelRef, isIntersecting: footerStuck } = useIntersectionObserver();
    const headerTextRef = useRef(null);
    const { subStepRef } = useFunnelSubStep();
    const { footerRef, headerRef, hasCustomHeight, hasCustomWidth, dialogCustomStyles, footerHeight } = useModalDimensions({
        height,
        width,
        hasFooter: !!footer,
    });
    return (React.createElement(FunnelNameSelectorContext.Provider, { value: `.${styles['header--text']}` },
        React.createElement(ResetContextsForModal, null,
            React.createElement(ModalContext.Provider, { value: {
                    isInModal: true,
                    componentLoadingCount,
                    emitTimeToContentReadyInModal,
                } },
                React.createElement("div", { ...baseProps, ...__funnelProps, ...__funnelStepProps, className: clsx(styles.root, { [styles.hidden]: !visible }, baseProps.className, isRefresh && styles.refresh), role: "dialog", "aria-labelledby": headerId, onMouseDown: onOverlayMouseDown, onClick: onOverlayClick, ref: mergedRef, style: footerHeight ? { scrollPaddingBottom: footerHeight } : undefined, "data-awsui-referrer-id": ((_a = subStepRef.current) === null || _a === void 0 ? void 0 : _a.id) || referrerId },
                    React.createElement(FocusLock, { disabled: !visible, autoFocus: true, restoreFocus: true, className: clsx(styles['focus-lock'], styles[`position-${position}`]) },
                        React.createElement("div", { className: clsx(styles.dialog, !hasCustomWidth && styles[size], styles[`breakpoint-${breakpoint}`], isRefresh && styles.refresh, hasCustomWidth && styles['custom-width'], hasCustomHeight && styles['custom-height']), style: dialogCustomStyles, onKeyDown: escKeyHandler, ...metadataAttribute },
                            React.createElement("div", { className: clsx(styles.container, hasCustomHeight && styles['custom-height-container']) },
                                React.createElement("div", { ref: headerRef, className: clsx(styles.header, analyticsSelectors.header) },
                                    React.createElement(InternalHeader, { variant: "h2", __disableActionsWrapping: true, actions: React.createElement("div", { ...getAnalyticsMetadataAttribute({
                                                action: 'dismiss',
                                            }) },
                                            React.createElement(InternalButton, { ariaLabel: closeAriaLabel, className: styles['dismiss-control'], variant: "modal-dismiss", iconName: "close", formAction: "none", onClick: onCloseButtonClick })) },
                                        React.createElement("span", { ref: headerTextRef, id: headerId, className: styles['header--text'] }, header))),
                                React.createElement(BuiltInErrorBoundary, { wrapper: content => React.createElement(InternalBox, { padding: { bottom: 'm', horizontal: 'l' } }, content) },
                                    React.createElement("div", { ref: __subStepRef, ...__subStepFunnelProps, className: clsx(styles.content, { [styles['no-paddings']]: disableContentPaddings }, hasCustomHeight && styles['custom-height-content']), ...(hasCustomHeight && {
                                            tabIndex: 0,
                                            role: 'region',
                                            'aria-labelledby': headerId,
                                        }) },
                                        children,
                                        React.createElement("div", { ref: stickySentinelRef })),
                                    footer && (React.createElement(ButtonContext.Provider, { value: { onClick: onButtonClick } },
                                        React.createElement("div", { ref: footerRef, className: clsx(styles.footer, (footerStuck || hasCustomHeight) && styles['footer--rounded']) }, footer))))))))))));
}
//# sourceMappingURL=internal.js.map